import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const Card = ({
	title,
	subtitle,
	icon: Icon,
	right,
	children,
	className = "",
	bodyClassName = "",
	darkMode,
	arClass,
	collapsible = false,
	defaultExpanded = true,
}) => {
	const [expanded, setExpanded] = useState(defaultExpanded);

	const shellClass = useMemo(() => {
		const base =
			"group relative h-full overflow-hidden rounded-xl border shadow-sm hover:shadow transition-all duration-200";
		const surface = darkMode
			? "bg-gray-900/50 border-white/10 backdrop-blur"
			: "bg-white border-slate-100";
		return `${base} ${surface} ${className}`.trim();
	}, [className, darkMode]);

	const headerClass =
		"flex items-start justify-between gap-3 px-5 sm:px-6 pt-5 sm:pt-6";
	const headerClickable = collapsible ? "cursor-pointer select-none" : "";

	return (
		<motion.section
			layout
			initial={{ opacity: 0, y: 14 }}
			animate={{ opacity: 1, y: 0 }}
			className={arClass ? arClass(shellClass) : shellClass}
			whileHover={{ scale: 1.003 }}
			whileTap={{ scale: 0.998 }}
			transition={{
				opacity: { duration: 0.2, ease: "easeOut" },
				y: { duration: 0.2, ease: "easeOut" },
				scale: { type: "spring", stiffness: 260, damping: 22 },
			}}
		>
			<div
				className={`${headerClass} ${headerClickable}`.trim()}
				onClick={
					collapsible ? () => setExpanded((prev) => !prev) : undefined
				}
			>
				<div className="flex items-start gap-3 min-w-0 flex-1">
					{Icon && (
						<div
							className={`mt-1 rounded-lg p-2 ${
								darkMode
									? "bg-white/10 text-white"
									: "bg-indigo-50 text-indigo-600"
							}`}
						>
							<Icon className="h-4 w-4 sm:h-5 sm:w-5" />
						</div>
					)}
					<div className="min-w-0 flex-1">
						<h3
							className={`text-sm sm:text-base font-semibold ${
								darkMode ? "text-white" : "text-gray-900"
							}`}
						>
							{title}
						</h3>
						{subtitle && (
							<p
								className={`text-xs sm:text-sm mt-0.5 ${
									darkMode
										? "text-gray-400"
										: "text-gray-600"
								}`}
							>
								{subtitle}
							</p>
						)}
					</div>
				</div>

				<div className="flex items-center gap-3">
					{right && <div className="flex-shrink-0">{right}</div>}
					{collapsible && (
						<motion.div
							animate={{ rotate: expanded ? 180 : 0 }}
							transition={{ duration: 0.2 }}
						>
							<ChevronDown
								className={`h-4 w-4 sm:h-5 sm:w-5 ${
									darkMode
										? "text-gray-400"
										: "text-gray-400"
								}`}
							/>
						</motion.div>
					)}
				</div>
			</div>

			<AnimatePresence initial={false}>
				{!collapsible || expanded ? (
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: "auto" }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.2 }}
					>
						<div
							className={`px-5 sm:px-6 pb-5 sm:pb-6 border-t ${
								darkMode
									? "border-white/10"
									: "border-slate-100"
							} ${bodyClassName}`.trim()}
						>
							{children}
						</div>
					</motion.div>
				) : null}
			</AnimatePresence>
		</motion.section>
	);
};

export default Card;
